import React from "react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import Toggle from "modules/swiftboard/components/ui/Toggle";
import ActiveBadge from "../../../components/ActiveBadge";
import { DATA_TYPE_LABEL, FIELD_ROLE_LABEL } from "../../../features/builder.constants";
import { FORMAT_LABEL, FORMAT_OPTIONS } from "../features/source-field.constants";
import { canFillValues, dataTypeChoices, roleOptions } from "../features/source-field.utils";
import { CheckInput, NumberInput, ReadsCell, TextInput, ValuesCell } from "./FieldCells";

/**
 * Columns of the editable field grid. Edits stay on the page (`patch`) until Save changes (bulk update);
 * `valueOf(row, key)` is the edited value, else the saved one. Read-only when not `editable`.
 */
export const getFieldColumns = ({ editable, subjectType, dirtyIds, valueOf, patch, fillingId, onFill }) => [
  {
    key: "sort_order",
    label: "Order",
    width: "96px",
    sortable: true,
    render: (row) =>
      editable ? (
        <div className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dirtyIds.has(row.id) ? "bg-primary" : "bg-transparent"}`} aria-label={dirtyIds.has(row.id) ? "Unsaved changes" : undefined} />
          <NumberInput ariaLabel={`Order of ${row.field_key}`} value={valueOf(row, "sort_order")} onChange={(value) => patch(row, { sort_order: value })} />
        </div>
      ) : (
        <span className="text-xs text-text_secondary">{row.sort_order ?? 0}</span>
      ),
  },
  {
    key: "field_key",
    label: "Field Key",
    width: "170px",
    sortable: true,
    render: (row) => <span className="font-mono text-[11px] text-text_primary">{row.field_key}</span>,
  },
  {
    key: "label",
    label: "Label",
    width: "200px",
    sortable: true,
    render: (row) => (editable ? <TextInput ariaLabel={`Label of ${row.field_key}`} value={valueOf(row, "label")} onChange={(value) => patch(row, { label: value })} /> : <span className="text-xs">{row.label}</span>),
  },
  {
    key: "path",
    label: "Reads",
    width: "200px",
    sortable: true,
    render: (row) => <ReadsCell row={row} />,
  },
  {
    key: "field_role",
    label: "Role",
    width: "150px",
    sortable: true,
    render: (row) => {
      const dataType = valueOf(row, "data_type");
      return editable ? (
        <SearchableSelect compact ariaLabel={`Role of ${row.field_key}`} options={roleOptions(row, dataType, subjectType)} value={valueOf(row, "field_role")} onChange={(value) => value && patch(row, { field_role: value })} />
      ) : (
        <span className="text-xs">{FIELD_ROLE_LABEL[row.field_role] ?? row.field_role}</span>
      );
    },
  },
  {
    key: "data_type",
    label: "Data Type",
    width: "140px",
    sortable: true,
    render: (row) => {
      const choices = dataTypeChoices(row);
      return editable && choices ? (
        <SearchableSelect compact ariaLabel={`Data type of ${row.field_key}`} options={choices} value={valueOf(row, "data_type")} onChange={(value) => value && patch(row, { data_type: value })} />
      ) : (
        <span className="whitespace-nowrap text-xs text-text_secondary">{DATA_TYPE_LABEL[row.data_type] ?? row.data_type}</span>
      );
    },
  },
  {
    key: "format",
    label: "Format",
    width: "140px",
    sortable: true,
    render: (row) =>
      editable ? (
        <SearchableSelect compact isClearable placeholder="None" ariaLabel={`Format of ${row.field_key}`} options={FORMAT_OPTIONS} value={valueOf(row, "format") ?? ""} onChange={(value) => patch(row, { format: value || null })} />
      ) : (
        <span className="text-xs text-text_secondary">{FORMAT_LABEL[row.format] ?? "—"}</span>
      ),
  },
  {
    key: "enum_values",
    label: "Values",
    width: "130px",
    sortable: false,
    render: (row) => (
      <ValuesCell
        values={valueOf(row, "enum_values")}
        canFill={canFillValues(row) && !valueOf(row, "is_sensitive")}
        busy={fillingId === row.id}
        editable={editable}
        onFill={() => onFill(row)}
        onClear={() => patch(row, { enum_values: null })}
      />
    ),
  },
  {
    key: "is_filterable",
    label: "Filter",
    width: "70px",
    sortable: true,
    render: (row) => <CheckInput ariaLabel={`${row.field_key} can be filtered`} checked={valueOf(row, "is_filterable")} disabled={!editable} onChange={(checked) => patch(row, { is_filterable: checked })} />,
  },
  {
    key: "is_template_var",
    label: "Template",
    width: "84px",
    sortable: true,
    // A sensitive value never reaches a template.
    render: (row) => (
      <CheckInput
        ariaLabel={`${row.field_key} is a template variable`}
        checked={valueOf(row, "is_template_var")}
        disabled={!editable || valueOf(row, "is_sensitive")}
        onChange={(checked) => patch(row, { is_template_var: checked })}
      />
    ),
  },
  {
    key: "is_sensitive",
    label: "Sensitive",
    width: "84px",
    sortable: true,
    render: (row) => (
      <CheckInput
        ariaLabel={`${row.field_key} is sensitive`}
        checked={valueOf(row, "is_sensitive")}
        disabled={!editable}
        onChange={(checked) => patch(row, checked ? { is_sensitive: true, is_template_var: false } : { is_sensitive: false })}
      />
    ),
  },
  {
    key: "is_activated",
    label: "Active",
    width: "90px",
    sortable: true,
    render: (row) => (editable ? <Toggle size="sm" checked={valueOf(row, "is_activated") === true} onChange={(checked) => patch(row, { is_activated: checked })} /> : <ActiveBadge active={row.is_activated} />),
  },
];
